<template>
  <div
    class="mine-card"
    :class="[
      { opened: opened },
      { error: originStatus.isError },
      {'title-edit-mode': isTitleEditMode},
      { 'is-time-machine': isTimeMachine }
    ]">
    <div class="mine-card-header">
      <div class="mine-main-info" v-if="mineData.type !== 'whitePaper'">
        <template v-if="originStatus.isError">
          <div class="error-label error-control">
            <i
              class="icon icon--bell-till size-16"
              v-btf-tooltip:top.start="{
                  contents: $t('mine.report.errorNoData.errorDesc'),
                  offset: -12
                }"
            ></i>
            <span class="error-text">{{
                $t('mine.report.errorNoData.errorTag')
              }}</span>
          </div>
        </template>
        <!-- Data exists, Retry available -->
        <template v-else-if="originStatus.isReportError">
          <div class="error-label error-control">
            <i
              class="icon icon--bell-till size-16"
              v-btf-tooltip:top.start="{
                  contents: $t('mine.report.errorFail.errorDesc'),
                  trigger: 'click',
                  offset: -12
                }"
            ></i>
            <span class="error-text">{{
                $t('mine.report.errorFail.errorTag')
              }}</span>
          </div>
          <div
            v-if="reportType !== 'mashup'"
            class="retry-button error-control btf-button primary icon"
            @click.stop="retryReport"
          >
            <i class="icon icon--refresh-white"></i>
            {{ $t('common.retry') }}
          </div>
        </template>
        <div class="mine-label-list">
          <div
            v-for="(label, key) in labelList"
            :key="key"
            :class="['label', key, { 'has-icon': label.icon }, label.class]"
          >
            <i v-if="label.icon" :class="['icon', label.icon]"></i>
            <span v-if="label.text" class="label-text">{{ label.text }}</span>
          </div>
        </div>
      </div>
      <div class="mine-title">
        <template v-if="!isTitleEditMode || isContentCard">
          <span :class="isContentCard ? 'content-card__title': 'title'" @click.stop="startTitleEditing">
            <text-highlight
              :searchKeyword="keyword"
              :textItem="reportTitle"
              accentColor="#F46200"
            ></text-highlight>
          </span>
          <button class="title-edit-button" v-if="reportType !== 'mashup' && !isContentCard" @click="startTitleEditing">
            <i class="icon icon--edit-white-size14"></i>
          </button>
        </template>
        <ui-input-text
          v-else
          class="title-input"
          ref="titleInput"
          :type="'text'"
          :modelValue="newReportTitle"
          :modelError="titleEditError"
          :errorTooltip="titleEditError"
          :placeholder="''"
          :errorType="'iconOnly'"
          :autoFocus="true"
          @input="setReportTitle"
          @onEnter="handleTitleEnter"
          @onBlur="finishTitleEditing"
        />
      </div>
      <div class="date-view-info">
        <div class="date-info">{{ setRegDate }}</div>
        <div class="view-count-info">
          <i class="icon icon--views"></i>
          <span class="view-count-value">{{ mineData.viewCount }}</span>
        </div>
      </div>
    </div>
    <div class="mine-card-body">
      <div
        v-for="(col, key) in colData"
        :key="key"
        :class="['info-item', key]"
      >
        <div class="info-item-value">
          <p v-if="col.text" :class="['info-text', col.letterType , {'center ':isContentCard} ]">{{ col.text }}</p>
          <p v-if="col.secondLabel" class="second-label">
            {{ col.secondLabel }}
          </p>
          <p v-if="col.secondText" class="info-text">{{ col.secondText }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { REPORT_LINK } from '@/libs/constants'
import TextHighlight from '@/components/ui/TextHighlight'
import { mapState } from 'vuex'
import { editReportTitle } from '@/api/ai'
import UiInputText from '@/components/ui/UiInputText.vue'
import { formatToTimeZone } from 'date-fns-timezone'
import { getMineReportDisplayMeta, isTimeMachineReport } from '@/libs/mine-report-helper'

const REPORT_DETAIL_DATA = [
  { name: 'type', letterType: 'upper center' },
  { name: 'url', letterType: 'ellipsis--2' },
  { name: 'period', letterType: '' },
  { name: 'device', letterType: 'upper center' }
]

export default {
  name: 'MineListTableItem',
  components: {
    TextHighlight,
    UiInputText
  },
  props: {
    mineData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    timezone: {
      type: String,
      default: null
    },
    reportType: {
      type: String,
      default: null
    },
    opened: {
      type: Boolean,
      default: false
    },
    originStatus: {
      type: Object,
      require: true
    },
    keyword: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      mineColumn: [],
      compareKey: null,
      isMarketingOpened: false,
      isTitleEditMode: false,
      newReportTitle: '',
      titleEditError: ''
    }
  },
  computed: {
    ...mapState(['domain', 'userData', 'journeyOptions', 'journeyPeriod']),
    isContentCard () {
      return this.mineData.type === 'cxTrends'
    },
    reportTitle () {
      if (this.isContentCard) {
        return this.mineData.title.replace(/<br\s*\/?>/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      }

      if (this.mineData.version && this.mineData.version > 1) {
        let version = this.mineData.version
        version = version < 10 ? ` v. ${version}` : ` v.${version}`

        return version + ' ' + this.mineData.title
      } else if (this.isWeekly) {
        return `[${this.weeklyDate.year} ${this.weeklyDate.title}] ${this.weeklyDate.month} ${this.weeklyDate.week}`
      } else if (this.mineData.type === 'dashboard' && !this.mineData.title) {
        return this.$t('dashboard.ai.defaultTitle')
      } else {
        return this.mineData.title
      }
    },
    reportLink () {
      if (this.originStatus.isAnalysis) {
        return null
      } else {
        return REPORT_LINK + '/' + this.mineData.id
      }
    },
    labelList () {
      let labels = {}
      if (this.mineData.lang && !this.isContentCard) {
        labels['lang'] = {
          icon: `icon--flag ${this.mineData.lang}`,
          text: this.mineData.lang
        }
      }

      if (this.mineData.type === 'cxTrends' && this.mineData.tagList) {
        this.mineData.tagList.forEach(tag => {
          labels[tag] = {
            text: tag,
            class: 'red-border'
          }
        })
      }

      if (this.originStatus.isNew) {
        labels['new'] = {
          icon: 'icon--new-small'
        }
      }

      if (!this.originStatus.isError && this.originStatus.isAnalysis) {
        labels['analyzing'] = {
          icon: 'icon--analyzing',
          text: this.$t('mine.report.analyzing')
        }
      }

      return labels
    },
    reportDisplayMeta () {
      return getMineReportDisplayMeta(this.mineData)
    },
    isTimeMachine () {
      return isTimeMachineReport(this.mineData)
    },
    setRegDate () {
      const timeZone = 'Asia/Seoul'

      return formatToTimeZone(
        this.mineData.regDate + 'Z',
        'YYYY-MM-DD HH:mm:ss',
        { timeZone }
      )
    },
    isWeekly () {
      return this.mineData.reportType === 'weekly'
    },
    colData () {
      let colData = {}
      let originData = this.mineData

      for (let col of this.mineColumn) {
        colData[col.name] = { letterType: col.letterType }

        if (this.isContentCard) {
          colData[col.name]['text'] = col.name === 'type'
            ? 'CX Data Trend'
            : '-'
          continue
        }
        // 기간
        if (col.name === 'period') {
          colData[col.name].text = `${originData.startDate} ~ ${originData.endDate}`
        } else if (col.name === 'type') {
          colData[col.name]['text'] = this.reportDisplayMeta.tag
        } else if (col.name === 'url') {
          colData[col.name]['text'] = originData[col.name] ? `[${originData.matchType}] ${originData[col.name]}` : '-'
          if (this.showMatchString) {
            colData[col.name]['secondLabel'] = 'Text'
            colData[col.name]['secondText'] = originData['matchString'] ? originData['matchString'] : '-'
          }
        } else {
          colData[col.name]['text'] = originData[col.name]
            ? originData[col.name]
            : this.$t('common.all')
        }
      }
      return colData
    },
    showMatchString () {
      return ['ends', 'contains', 'regexp'].includes(this.mineData.matchType)
    }
  },
  watch: {
    mineData () {
      if (
        this.mineData.promptType === 'compare' &&
        this.mineData.orgList.length === 2
      ) {
        this.mineColumn.forEach(col => {
          let result
          if (col.name === 'period') {
            result =
              [
                this.mineData.orgList[0].startDateStr,
                this.mineData.orgList[0].endDateStr
              ].join('~') !==
              [
                this.mineData.orgList[1].startDateStr,
                this.mineData.orgList[1].endDateStr
              ].join('~')
          } else {
            result =
              this.mineData.orgList[0][col.name] !==
              this.mineData.orgList[1][col.name]
          }
          if (result) {
            this.compareKey = col.name
          }
        })
      }
    }
  },
  created () {
    this.mineColumn = REPORT_DETAIL_DATA
  },
  mounted () {
  },
  deactivated () {
    if (this.isTitleEditMode) {
      this.exitTitleEditing()
    }
  },
  methods: {
    retryReport () {
      this.$emit('retry', this.mineData)
    },
    setReportTitle (value) {
      if (value.length > 70) {
        this.titleEditError = this.$t('mine.report.maxLengthError', { limit: 70 })
      } else {
        this.titleEditError = ''
      }
      this.newReportTitle = value
    },
    startTitleEditing () {
      if (this.reportType === 'mashup') return
      this.newReportTitle = this.reportTitle
      this.isTitleEditMode = true
      this.$emit('changeTitleEditMode', this.mineData.id, true)
    },
    handleTitleEnter () {
      this.$refs.titleInput.$refs.input.blur()
    },
    finishTitleEditing () {
      if (this.titleEditError) return
      if (!this.newReportTitle.trim()) {
        this.exitTitleEditing()
        return
      }
      this.isTitleEditMode = false
      this.$emit('changeTitleEditMode', this.mineData.id, false)
      if (this.reportTitle === this.newReportTitle) return
      editReportTitle({
        reportId: this.mineData.id,
        title: this.newReportTitle
      }).then(res => {
        this.mineData.title = this.newReportTitle
      }).catch(err => {
        console.log(err)
      })
    },
    exitTitleEditing () {
      this.isTitleEditMode = false
      this.titleEditError = ''
      this.newReportTitle = ''
      this.$emit('changeTitleEditMode', this.mineData.id, false)
    }
  }
}
</script>

<style lang="scss" scoped>
$errorColor: #e60724;
$mashupColor: #ff30ea;
$reportColor: #0085ff;
$analyzeColor: #b65efc;

.mine-card {
  height: 108px;
  border-bottom: 1px solid #ebebeb;
  font-weight: 400;
  line-height: 1;
  box-sizing: border-box;

  & * {
    transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
  }

  .mine-card-header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    height: 100%;
    width: calc(100% - 290px);
    padding-left: 12px;

    .mine-main-info {
      display: flex;
      gap: 8px;
      width: 100%;
      font-size: 12px;
      min-height: 8px;
      overflow: hidden;
      .error-label {
        padding: 2px 8px 2px 4px;
        display: flex;
        align-items: center;
        gap: 5px;
        background-color: #f4e43a;
        border-radius: 5px;
        color: #444;
        opacity: 1;
        i.icon {
          background-color: #fff;
          border-radius: 50%;
        }

        .error-text {
          white-space: nowrap;
        }
      }

      .retry-button {
        position: absolute;
        top: 50%;
        left: calc((100% - 200px)/2);
        transform: translate(-50%, -50%);
        z-index: 3;
      }

      .mine-label-list {
        display: flex;
        align-items: center;
        display: inline-flex;
        gap: 8px;
        .label {
          font-weight: 600;
          color: #666;
          border-radius: 4px;
          &.red-border {
            border-radius: 4px;
            border: 1px solid #D7D7D7;
            background: #FFF;
            padding: 4px 6px;
            color: #EC0047;
            font-size: 12px;
            font-weight: 600;
            line-height: 100%;
          }

          &.has-icon {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          &.lang {
            gap: 2px;
            padding: 2px 3px;
            background: #fff;
            border: 1px solid rgba(0, 0, 0, 0.12);
            text-transform: uppercase;
          }
          &.weekly {
            padding: 4px 6px;
            border: 1px solid rgba(0, 0, 0, 0.12);
          }
          &.compare {
            gap: 2px;
            padding: 2px 6px 2px 4px;
            background-color: $brandColor;
            color: #fff;
          }
          &.mashup {
            gap: 4px;
            font-size: 13px;
          }
          &.analyzing {
            gap: 4px;
            color: #00000070;
            font-size: 13px;
            font-weight: 600;
          }
          .label-text {
            white-space: nowrap;
          }
        }
      }
    }
    .mine-title {
      width: fit-content;
      position: relative;
      min-height: 28px;
      padding-right: 40px;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.5;
      margin-top: -1px;
      transition: none;
      color: #444;
      .content-card__title {
        overflow: hidden;
        text-overflow: ellipsis;
        overflow-wrap: break-word;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
      }
      .title {
        width: fit-content;
        overflow: hidden;
        text-overflow: ellipsis;
        overflow-wrap: break-word;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        transition: none;
      }
      .title-edit-button {
        display: none;
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        justify-content: center;
        align-items: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: #111;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.20);
        cursor: pointer;
        &:hover {
          background-color: #2F2F2F;
        }
      }
      /deep/ .title-input {
        margin: 1px 0px;
        pointer-events: auto;
        max-width: 288px;
        .label {
          display: none;
        }
        .input-element {
          input {
            height: 32px;
            border-radius: 5px;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.20);
            font-size: 13px;
            line-height: 32px;
          }
          &:not(.error) {
            input {
              border-color: transparent;
            }
          }
        }
      }
    }
    .date-view-info {
      display: flex;
      align-items: center;
      gap: 17px;
      font-size: 12px;
      overflow: hidden;
      color: #000;
      .date-info {
        opacity: 0.44;
        white-space: nowrap;
      }
      .view-count-info {
        display: flex;
        align-items: center;
        gap: 4px;
        position: relative;
        white-space: nowrap;
        .view-count-value {
          opacity: 0.44;
        }

        &:before {
          content: '';
          position: absolute;
          left: -8px;
          top: 50%;
          transform: translateY(-50%);
          display: inline-block;
          width: 1px;
          height: 8px;
          border-left: 1px solid #0003;
        }
      }
    }
  }
  .mine-card-body {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 6px;
    height: calc(100% - 1px);
    box-sizing: border-box;
    transform: translateX(423px);
    z-index: 1;
    &:before {
      position: absolute;
      content: '';
      top: 0;
      left: -4px;
      width: 4px;
      height: calc(100% + 1px);
      background: linear-gradient(
        270deg,
        rgba(0, 0, 0, 0.15) 0%,
        rgba(0, 0, 0, 0) 100%
      );
      border-right: 1px solid #BBB;
      box-sizing: border-box;
    }

    .info-item {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      color: #444;
      font-size: 13px;
      line-height: 120%;
      > :nth-child(2) {
        padding-top: 8px;
        margin-top: 8px;
        border-top: 1px dashed #ccc;
      }
      .info-item-value {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
      .info-text {
        width: 100%;
        &.center {
          text-align: center;
        }
        &.upper {
          text-transform: capitalize;
        }
      }
      .second-label {
        padding: 6px 0 0 0;
        color: #999;
        font-size: 13px;
        font-weight: 600;
        line-height: 120%;
      }
      .marketing-info-btn {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 5px;
        &.show i {
          transform: rotate(180deg);
        }
      }
      &.upper {
        text-transform: capitalize;
      }
      &.center {
        text-align: center;
      }
      &.type {
        width: 78px;
        padding-left: 20px;
        color: $brandColor;
        text-align: center;
        font-weight: 600;
        transform: translateX(20px);
      }
      &.url {
        width: 249px;
        padding: 0 15px;
        transform: translateX(190px);
      }
      &.period {
        width: 91px;
        padding: 0 12px;
        transform: translateX(180px);
      }
      &.device {
        width: 50px;
        padding: 0 253px 0 12px;
      }
    }
  }

  &.opened {
    .mine-card-header {
      width: calc(100% - 825px);
      .mine-main-info {
        .retry-button {
          right: calc(726px / 2);
          left: auto;
          transform: translate(50%, -50%);
        }
      }
    }
    .mine-card-body {
      transform: translateX(0);
      &:before {
        left: -12px;
        width: 12px;
      }

      .info-item {
        &.type {
          transform: translateX(0);
        }
        &.url {
          padding: 0 15px;
          transform: translateX(0);
        }
        &.period {
          transform: translateX(0);
        }
      }
    }
  }
  &.error {
    .mine-card-header {
      .mine-label-list {
        opacity: 0.3;
        .label.compare {
          color: #555;
          background-color: #bcbcbc;
        }
      }
      .mine-title {
        opacity: 0.3;
        &:hover {
          opacity: 0.8;
        }
        .title-edit-button {
          opacity: 1;
        }
      }
      .date-view-info {
        opacity: 0.3;
      }
    }
    .mine-card-body .info-item {
      opacity: 0.3;
    }
  }
  &.title-edit-mode {
    .mine-card-header {
      .mine-title {
        width: 100%;
      }
    }
  }
  &:not(.mashup) {
    .mine-card-header {
      .mine-title {
        &:hover {
          cursor: pointer;
          .title {
            background-color: rgba(0, 0, 0, 0.16);
            box-shadow: -4px 0 0 rgba(0, 0, 0, 0.16), 6px 0 0 rgba(0, 0, 0, 0.16);
            border-radius: 4px;
          }
          .title-edit-button {
            display: flex;
          }
        }
      }
    }
  }
}

.ellipsis {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  word-break: break-all;

  &--2,
  &--3 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    word-break: break-all;
    overflow-wrap: break-word;
  }

  &--2 {
    -webkit-line-clamp: 2;
  }

  &--3 {
    -webkit-line-clamp: 3;
  }
}

/* 타임머신 리포트 : 리스트(테이블) 뷰의 타입 텍스트만 블루 (카드뷰와 동일 컬러) */
.mine-card.is-time-machine .mine-card-body .info-item.type {
  color: $timeMachineColor;
}

.ghost {
  .mine-card {
    .mine-card-header {
      width: calc(100% - 250px);
    }
    .mine-card-body {
      transform: translateX(594px);
    }
    &.opened {
      .mine-card-header {
        width: calc(100% - 735px);
      }
      .mine-card-body {
        transform: translateX(110px);
      }
    }
  }
}
</style>
